import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  OnDestroy,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideBan,
  lucideCalendarX,
  lucideChevronDown,
  lucideCircleCheck,
  lucideClock,
  lucideDownload,
  lucideGift,
  lucideMinus,
  lucidePlus,
  lucideRotateCcw,
  lucideTriangleAlert,
  lucideX,
} from '@ng-icons/lucide';
import { HlmAccordionImports } from '@spartan-ng/helm/accordion';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmBadgeImports, type BadgeVariants } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmProgressImports } from '@spartan-ng/helm/progress';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTabsImports } from '@spartan-ng/helm/tabs';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { LoggerService } from '../../services/logger.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TokenStorageService } from '../../services/token-storage.service';
import { VoucherService } from '../../services/voucher.service';
import type { Voucher } from '../../services/voucher.service';
import { lastValueFrom } from 'rxjs';
import { MaskitoDirective } from '@maskito/angular';
import { CompanyService } from '../../services/company.service';
import { EURO_AMOUNT_MASK, formatCurrency, parseEuroToCents } from '../../utils/currency.utils';

/** One dot on the vertical history timeline. */
export interface VoucherTimelineEvent {
  icon: string;
  title: string;
  date: string | null;
  detail?: string;
  /** Signed amount, already formatted (e.g. "+50,00 €" / "−12,50 €"). */
  amount?: string;
  /** Remaining balance after this event, when the API reports it. */
  balance?: string;
  /** Colour of the dot — the last event carries the voucher's current state. */
  tone: 'neutral' | 'primary' | 'muted' | 'destructive';
}

@Component({
  selector: 'app-voucher-details',
  standalone: true,
  imports: [
    RouterModule,
    MaskitoDirective,
    ReactiveFormsModule,
    NgIcon,
    HlmAccordionImports,
    HlmAlertImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmFieldImports,
    HlmInputGroupImports,
    HlmItemImports,
    HlmProgressImports,
    HlmSkeletonImports,
    HlmSpinnerImports,
    HlmTabsImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      lucideBan,
      lucideCalendarX,
      lucideChevronDown,
      lucideCircleCheck,
      lucideClock,
      lucideDownload,
      lucideGift,
      lucideMinus,
      lucidePlus,
      lucideRotateCcw,
      lucideTriangleAlert,
      lucideX,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './voucher-details.component.html',
})
export class VoucherDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() voucherCode: string | undefined = undefined;
  isLoading: boolean = true;
  voucher: Voucher | null = null;
  error: string = '';
  successMessage: string = '';
  successVisible: boolean = false;
  isRedeeming: boolean = false;
  isDownloading: boolean = false;
  partialRedemptionForm: FormGroup;
  showPartialError: boolean = false;
  activeTab: 'details' | 'history' = 'details';
  showPartialForm: boolean = false;
  /** Built once per load rather than in a getter — see `buildTimeline`. */
  timeline: VoucherTimelineEvent[] = [];
  private successTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly tokenStorage = inject(TokenStorageService);
  private readonly companyService = inject(CompanyService);
  readonly euroMask = EURO_AMOUNT_MASK;

  /** A network only sells; its members redeem. So it gets no redeem buttons. */
  get canRedeem(): boolean {
    return !this.companyService.company()?.is_group_admin;
  }

  /** The PDF carries the QR code: only the voucher's own company may download it. */
  get isOwnVoucher(): boolean {
    return String(this.voucher?.public_company_id) === this.tokenStorage.getPublicCompanyId();
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private voucherService: VoucherService,
    private formBuilder: FormBuilder,
    private logger: LoggerService,
  ) {
    this.partialRedemptionForm = this.formBuilder.group({
      amount: ['', Validators.required],
    });
  }

  ngOnInit() {
    if (this.voucherCode) {
      this.loadVoucherDetails(this.voucherCode);
    } else {
      this.error = 'Gutscheincode nicht gefunden';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['voucherCode'] && this.voucherCode) {
      this.successMessage = '';
      this.successVisible = false;
      this.error = '';
      this.activeTab = 'details';
      this.showPartialForm = false;
      this.showPartialError = false;
      this.timeline = [];
      this.loadVoucherDetails(this.voucherCode);
    }
  }

  loadVoucherDetails(code: string) {
    this.isLoading = true;

    lastValueFrom(this.voucherService.getVoucherByCode(code))
      .then((data) => {
        this.voucher = data;
        this.timeline = this.buildTimeline(data);
        this.error = '';
      })
      .catch((error) => {
        this.logger.error('Error loading voucher details:', error);
        if (error.status === 401) {
          this.router.navigate(['/login']);
        } else {
          this.error = 'Fehler beim Laden der Gutscheindetails';
        }
      })
      .finally(() => {
        this.isLoading = false;
        // Rendered inside the OnPush table's dialog portal: without this the
        // whole overlay subtree is skipped and the skeleton never goes away.
        this.cdr.markForCheck();
      });
  }

  private showSuccess(message: string) {
    this.successMessage = message;
    this.successVisible = true;
    if (this.successTimer) clearTimeout(this.successTimer);
    this.successTimer = setTimeout(() => {
      this.successVisible = false;
      this.cdr.markForCheck();
    }, 5000);
  }

  ngOnDestroy() {
    if (this.successTimer) clearTimeout(this.successTimer);
  }

  redeemFull() {
    if (!this.voucher || this.isRedeeming) return;

    const code = this.voucherCode!;
    this.isRedeeming = true;
    this.error = '';

    this.voucherService.redeemVoucher(this.voucher.verification_key!).subscribe({
      next: () => {
        this.isRedeeming = false;
        this.showSuccess('Gutschein wurde vollständig eingelöst!');
        this.loadVoucherDetails(code);
      },
      error: (error) => {
        this.logger.error('Error redeeming voucher:', error);
        this.error = error.error?.detail || 'Fehler beim Einlösen des Gutscheins';
        this.isRedeeming = false;
        this.cdr.markForCheck();
      },
    });
  }

  redeemPartial() {
    if (!this.voucher || this.isRedeeming || this.partialRedemptionForm.invalid) return;

    const amountInCents = parseEuroToCents(this.partialRedemptionForm.get('amount')?.value);

    if (
      amountInCents === null ||
      amountInCents <= 0 ||
      amountInCents > (this.voucher.amount || 0)
    ) {
      this.showPartialError = true;
      return;
    }

    const code = this.voucherCode!;
    this.showPartialError = false;
    this.isRedeeming = true;
    this.error = '';

    this.voucherService
      .redeemPartialVoucher(this.voucher.verification_key!, amountInCents)
      .subscribe({
        next: () => {
          this.isRedeeming = false;
          this.partialRedemptionForm.reset();
          this.showPartialForm = false;
          this.showSuccess(`${this.formatAmount(amountInCents)} € erfolgreich eingelöst!`);
          this.loadVoucherDetails(code);
        },
        error: (error) => {
          this.logger.error('Error redeeming voucher partially:', error);
          this.error = error.error?.detail || 'Fehler beim teilweisen Einlösen des Gutscheins';
          this.isRedeeming = false;
          this.cdr.markForCheck();
        },
      });
  }

  get statusLabel(): string {
    switch (this.voucher?.status) {
      case 'redeemed':
        return 'Eingelöst';
      case 'expired':
        return 'Abgelaufen';
      case 'refunded':
        return 'Erstattet';
      default:
        return 'Aktiv';
    }
  }

  get statusVariant(): BadgeVariants['variant'] {
    switch (this.voucher?.status) {
      case 'redeemed':
        return 'secondary';
      case 'expired':
      case 'refunded':
        return 'destructive';
      default:
        return 'solid';
    }
  }

  /**
   * The big figure means something different once the voucher is closed —
   * "verfügbares Guthaben" reads wrong on a refunded or expired voucher.
   */
  get balanceLabel(): string {
    switch (this.voucher?.status) {
      case 'redeemed':
        return 'Vollständig eingelöst';
      case 'refunded':
        return 'Erstattet — kein Guthaben';
      case 'expired':
        return 'Abgelaufen — nicht mehr einlösbar';
      default:
        return 'Verfügbares Guthaben';
    }
  }

  /** How much of the original value is already used up, in cents. */
  get redeemedAmount(): number {
    if (!this.voucher) return 0;
    return Math.max(0, (this.voucher.initial_amount ?? 0) - (this.voucher.amount ?? 0));
  }

  get redeemedPercent(): number {
    const initial = this.voucher?.initial_amount ?? 0;
    if (initial <= 0) return 0;
    return Math.min(100, Math.round((this.redeemedAmount / initial) * 100));
  }

  get hasRedemptions(): boolean {
    return (this.voucher?.redemptions?.length ?? 0) > 0;
  }

  /**
   * Creation → every redemption → where the voucher stands now.
   *
   * Built eagerly on load instead of as a getter: this component still runs on
   * the eager change detection strategy, and a getter would rebuild the whole
   * array on every check.
   */
  private buildTimeline(voucher: Voucher): VoucherTimelineEvent[] {
    const events: VoucherTimelineEvent[] = [];

    events.push({
      icon: 'lucidePlus',
      title: voucher.is_internal ? 'Intern erstellt' : 'Gutschein gekauft',
      date: voucher.created_at ? this.formatDate(voucher.created_at) : null,
      detail: voucher.is_internal
        ? (voucher.internal_note ?? 'Keine Zahlung, keine Gebühren')
        : (voucher.recipient_name ?? undefined),
      amount: `+ ${this.formatAmount(voucher.initial_amount)} €`,
      tone: 'primary',
    });

    // The API returns redemptions newest-first; the timeline reads top-down.
    const redemptions = [...(voucher.redemptions ?? [])].sort(
      (a, b) => new Date(a.redeemed_at).getTime() - new Date(b.redeemed_at).getTime(),
    );

    for (const redemption of redemptions) {
      const isFull = redemption.balance_after === 0;
      events.push({
        icon: isFull ? 'lucideCircleCheck' : 'lucideMinus',
        title: isFull ? 'Vollständig eingelöst' : 'Teileinlösung',
        date: this.formatDate(redemption.redeemed_at),
        detail: redemption.redeeming_company_name ?? undefined,
        amount: `− ${this.formatAmount(redemption.amount)} €`,
        balance:
          redemption.balance_after != null
            ? `Rest: ${this.formatAmount(redemption.balance_after)} €`
            : undefined,
        tone: 'neutral',
      });
    }

    // A final redemption that hit zero already says "vollständig eingelöst" —
    // appending the status dot as well would just repeat it. Real dev data has
    // status 'redeemed' with a null redeemed_at, so the duplicate carried no
    // date either.
    const lastZeroed = redemptions.at(-1)?.balance_after === 0;
    if (!(voucher.status === 'redeemed' && lastZeroed)) {
      events.push(this.currentStateEvent(voucher));
    }

    return events;
  }

  /** The closing dot: what is true about this voucher right now. */
  private currentStateEvent(voucher: Voucher): VoucherTimelineEvent {
    switch (voucher.status) {
      case 'redeemed':
        return {
          icon: 'lucideCircleCheck',
          title: 'Vollständig eingelöst',
          date: voucher.redeemed_at ? this.formatDate(voucher.redeemed_at) : null,
          tone: 'muted',
        };
      case 'refunded':
        return {
          icon: 'lucideRotateCcw',
          title: 'Erstattet',
          date: voucher.refunded_at ? this.formatDate(voucher.refunded_at) : null,
          tone: 'destructive',
        };
      case 'expired':
        return {
          icon: 'lucideCalendarX',
          title: 'Abgelaufen',
          date: voucher.expires_at ? this.formatDate(voucher.expires_at) : null,
          tone: 'destructive',
        };
      default:
        return {
          icon: 'lucideClock',
          title: 'Aktiv',
          date: voucher.expires_at ? `Gültig bis ${this.formatDate(voucher.expires_at)}` : null,
          detail: `Restguthaben ${this.formatAmount(voucher.amount)} €`,
          tone: 'primary',
        };
    }
  }

  /** Dot styling per tone — kept out of the template to keep it readable. */
  dotClass(tone: VoucherTimelineEvent['tone']): string {
    switch (tone) {
      case 'primary':
        return 'bg-primary text-primary-foreground';
      case 'destructive':
        return 'bg-destructive text-white';
      case 'muted':
        return 'bg-muted-foreground text-background';
      default:
        return 'bg-secondary text-secondary-foreground';
    }
  }

  formatAmount(amount: number | undefined): string {
    return formatCurrency(amount);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  downloadPdf() {
    if (!this.voucher || this.isDownloading) return;
    this.isDownloading = true;
    this.voucherService.downloadVoucherPDF(this.voucher.code).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gutschein-${this.voucher!.code}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.isDownloading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.logger.error('Error downloading voucher PDF:', err);
        this.error = 'Fehler beim Herunterladen des Gutscheins';
        this.isDownloading = false;
        this.cdr.markForCheck();
      },
    });
  }

  goBack() {
    this.router.navigate(['/voucher-dashboard']);
  }

  isProductVoucher(): boolean {
    if (!this.voucher) return false;
    return this.voucherService.isProductVoucher(this.voucher);
  }

  isMoneyVoucher(): boolean {
    if (!this.voucher) return false;
    return this.voucherService.isMoneyVoucher(this.voucher);
  }

  getProductImageUrl(): string | null {
    if (!this.voucher?.voucher_product_data?.image_cropped_path) {
      return null;
    }
    return this.voucherService.getProductImageUrl(
      this.voucher.voucher_product_data.image_cropped_path,
    );
  }
}
